import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { cn } from "@/src/lib/utils";
import { Booking } from "./model";
import { charterProfile } from "./charterProfile";
import { CheckIcon, ClockIcon } from "lucide-react";

/**
 * Sticky booking header (unified booking page brief, 16 Sep): answers "is this
 * charter ready, and where's the money" without scrolling.
 *
 * Gates read live off state already on the page — marking a contract signed or
 * an addendum signed updates the chip immediately. The paid bars reuse the
 * Payments section's query keys, so opening a booking makes no extra request.
 */

type Gate = { key: string; ok: boolean; note: string };

const fmtMoney = (v: number, code?: string | null) =>
  new Intl.NumberFormat("en-EN", { style: "currency", currency: code || "EUR", maximumFractionDigits: 0 }).format(v || 0);

const sumPaid = (rows: any[] | undefined) =>
  (rows ?? []).reduce((t, r) => t + (Number(r?.paidSoFar) || 0), 0);
const sumDue = (rows: any[] | undefined) =>
  (rows ?? []).reduce((t, r) => t + (Number(r?.amount) || 0), 0);

function PaidBar({
  label,
  paid,
  total,
  currency,
  tone,
  onClick,
}: {
  label: string;
  paid: number;
  total: number;
  currency?: string | null;
  tone: "client" | "supplier";
  onClick: () => void;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <button type="button" onClick={onClick} className="min-w-[180px] flex-1 text-left">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="block text-base font-semibold tabular-nums text-slate-900">{fmtMoney(paid, currency)}</span>
      <span className="mt-1 block h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <span
          className={cn("block h-full rounded-full", tone === "client" ? "bg-green-600" : "bg-amber-500")}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="text-[11px] text-slate-400">
        {total > 0 ? `${pct}% of ${fmtMoney(total, currency)}` : "no schedule yet"}
      </span>
    </button>
  );
}

export function BookingHeader({
  booking,
  sections,
  activeSection,
  onGoToSection,
  bankDetailsOnFile,
}: {
  booking: Booking;
  sections: { id: string; label: string }[];
  activeSection: string;
  onGoToSection: (id: string) => void;
  bankDetailsOnFile: boolean;
}) {
  const profile = charterProfile(booking);

  // Same keys and fetcher as the Payments section — React Query serves both.
  const { data: clientSchedule } = useQuery({
    queryKey: ["paymentScheduleCustomerData", booking.id],
    queryFn: () =>
      paymentsFetch<{ data: any[] }>(
        `/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=Customer&bookingRef=${booking.id}&operatorRef=null`,
      ),
    enabled: !!booking.id,
  });
  const { data: supplierSchedule } = useQuery({
    queryKey: ["paymentScheduleOperatorData", booking.id],
    queryFn: () =>
      paymentsFetch<{ data: any[] }>(
        `/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=Operator&bookingRef=${booking.id}&operatorRef=null`,
      ),
    enabled: !!booking.id,
  });

  const clientPaid = sumPaid(clientSchedule?.data);
  const clientTotal = sumDue(clientSchedule?.data);
  const supplierPaid = sumPaid(supplierSchedule?.data);
  const supplierTotal = sumDue(supplierSchedule?.data);

  const gates: Gate[] = useMemo(() => {
    const status = booking.contractStatus ?? (booking.contractSigned ? "client_signed" : "none");
    const addendums = booking.addendumRecords ?? [];
    const outstandingAddendums = addendums.filter((a) => a.state !== "signed");
    const clientOwed = Math.max(0, clientTotal - clientPaid);
    const supplierOwed = Math.max(0, supplierTotal - supplierPaid);

    const list: Gate[] = [
      {
        key: "Contract",
        ok: status === "countersigned",
        note:
          status === "countersigned"
            ? "Countersigned"
            : status === "none"
              ? "Not sent yet"
              : `Awaiting countersignature (${String(status).split("_").join(" ")})`,
      },
      {
        key: "Addendums",
        ok: outstandingAddendums.length === 0,
        note: addendums.length === 0 ? "None" : `${outstandingAddendums.length} outstanding`,
      },
      {
        key: "Client funds",
        ok: clientTotal > 0 && clientOwed <= 0.01,
        note:
          clientTotal <= 0
            ? "No client schedule yet"
            : clientOwed > 0.01
              ? `${fmtMoney(clientOwed, booking.currency?.code)} still due`
              : "Settled",
      },
      {
        key: `${profile.supplierShort} paid`,
        ok: supplierTotal > 0 && supplierOwed <= 0.01,
        note:
          supplierTotal <= 0
            ? "No supplier schedule yet"
            : supplierOwed > 0.01
              ? `${fmtMoney(supplierOwed, booking.operatorTransfer?.currencyCode)} still to pay`
              : "Paid in full",
      },
    ];
    // Bareboat pays the base locally, so bank details aren't a gate there.
    if (profile.needsBankDetails) {
      list.push({
        key: "Bank details",
        ok: bankDetailsOnFile,
        note: bankDetailsOnFile ? "On file" : `Blocks the ${profile.supplierLabel.toLowerCase()} payment`,
      });
    }
    list.push({
      key: "Documents",
      ok: !!booking.contractTemplate || status !== "none",
      note: booking.contractTemplate || status !== "none" ? "Agreement on file" : "No agreement on file yet",
    });
    return list;
  }, [booking, clientPaid, clientTotal, supplierPaid, supplierTotal, bankDetailsOnFile, profile]);

  const clear = gates.filter((g) => g.ok).length;
  const ready = clear === gates.length;

  return (
    <div className="z-30 border-b border-slate-200 bg-white px-6 py-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span
          className={cn(
            "rounded-full px-2.5 py-1 text-sm font-medium",
            ready ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800",
          )}
        >
          <span className="inline-flex items-center gap-1">{ready ? <CheckIcon className="h-3.5 w-3.5" /> : <ClockIcon className="h-3.5 w-3.5" />}{ready ? "Charter ready" : `${clear} of ${gates.length} ready`}</span>
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {gates.map((g) => (
            <span
              key={g.key}
              title={g.note}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                g.ok ? "border-green-200 bg-green-50 text-green-800" : "border-amber-200 bg-amber-50 text-amber-800",
              )}
            >
              {g.ok ? <CheckIcon className="h-3 w-3" /> : <ClockIcon className="h-3 w-3" />}
              {g.key}
            </span>
          ))}
        </div>
        <div className="ml-auto flex min-w-[380px] flex-1 items-start gap-4">
          <PaidBar
            label="Client paid"
            paid={clientPaid}
            total={clientTotal}
            currency={booking.currency?.code}
            tone="client"
            onClick={() => onGoToSection("money")}
          />
          <PaidBar
            label={`${profile.supplierLabel} paid`}
            paid={supplierPaid}
            total={supplierTotal}
            currency={booking.operatorTransfer?.currencyCode ?? booking.currency?.code}
            tone="supplier"
            onClick={() => onGoToSection("money")}
          />
        </div>
      </div>
      <div className="mt-2 flex flex-row flex-wrap gap-1" role="tablist">
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={activeSection === s.id}
            onClick={() => onGoToSection(s.id)}
            className={cn(
              "whitespace-nowrap rounded-[5px] px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100",
              activeSection === s.id && "bg-slate-100 text-slate-900",
              s.id === "confirm" && "text-red-700 hover:bg-red-50",
              s.id === "confirm" && activeSection === s.id && "bg-red-50 text-red-800",
            )}
          >
            {s.label}
            {s.id === "confirm" && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">action required</span>}
          </button>
        ))}
      </div>
    </div>
  );
}
